import { logUsage, PerformanceDatapoint } from 'owa-analytics';
import { LinkIdContainer } from 'owa-attachment-link-common';
import { lazyGetAttachmentSuggestionsTraceIdByComposeId } from 'owa-attachment-suggestions';
import { addComposeLinkViewState, createSharingLinkView } from 'owa-compose-link';
import { getExtensionWithoutDotFromFileNameForLogging } from 'owa-file';
import { LinkProviderType } from 'owa-fileprovider-link';
import { GetSharingInfoResponseBase } from 'owa-fileprovider-link-services';
import { getSharingInformation, onSharingLinkDetected } from 'owa-link-core';
import {
    addSharingLinkToContianer,
    AnchorElementsSource,
    getLinkIdFromAnchorElementId,
    getSharingLinkInfo,
    SharingLinkInfo,
} from 'owa-link-data';
import { isStringNullOrWhiteSpace } from 'owa-localize';
import { lazyTryCheckPermForOneLink } from 'owa-recipient-permission-checker';
import { lazyLogEntitiesFromFilePicker } from 'owa-search-instrumentation';
import SubstrateSearchScenario from 'owa-search-service/lib/data/schema/SubstrateSearchScenario';
import AttachmentResultCode from 'owa-service/lib/contract/AttachmentResultCode';
import GetSharingInfoResponse from 'owa-service/lib/contract/GetSharingInfoResponse';
import SharingLinkKind from 'owa-service/lib/contract/SharingLinkKind';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import * as trace from 'owa-trace';
import LinkPlugin from '../../plugin/LinkPlugin';
import { addBeautifiedPrefixToId } from '../beautifiedPrefix';
import { tryGetUnwrappedUrl } from './tryGetUnwrappedUrl';
import { isPureAnchorElement } from '../processAnchorElementsFromHtmlContent';
import { displaySpinnerWhenShouldBeautifyLink } from './displaySpinnerWhenShouldBeautifyLink';
import { getSharingLinkInfoAndVerifyHref } from './getSharingLinkInfoAndVerifyHref';
import { prefetchGetWACInfo } from './prefetchGetWACInfo';
import { removeSpinnerWhenShouldBeautifyLink } from './removeSpinnerWhenShouldBeautifyLink';
import { stopReasonAfterGetSharingInfo } from './stopReasonAfterGetSharingInfo';
import { updateSharePointLinkPermissionInDraft } from './updateSharePointLinkPermissionInDraft';

// In some cases there are multiple editor events that detect the same link at about the same time.
// We keep track of links that we are fetching sharing info for, to prevent duplicate calls for the
// same link.
const pendingLinkIds = [];
export async function getSharingInfoToTryUpdateLink(
    linkPlugin: LinkPlugin,
    providerType: LinkProviderType,
    shouldBeautifyLink: boolean, // We would display a spinner during server call, change the text, add file icon, display error icon & red text for links with sharing issues, context menu on click
    shouldRehydrate: boolean, // We won't change the link, but display error icon & red text for links with sharing issues, context menu on click
    isPreviouslyBeautified: boolean,
    anchorElement: HTMLAnchorElement,
    linkIdContainer: LinkIdContainer,
    linkIndex: number, // The link index when there are a collection of anchor elements; otherwise, just put 0
    linkIdsWithSpinner: string[],
    anchorElementsSource: AnchorElementsSource,
    targetWindow: Window,
    isCalendar: boolean
): Promise<void> {
    if (!linkPlugin.editor) {
        return;
    }
    let datapoint: PerformanceDatapoint;

    // We define a customData object to make sure that the same customData properties are
    // logged in the same order no matter what code path is taken in this file.
    const customData = {
        anchorElementsSource: anchorElementsSource,
        canceledReason: '',
        didBeautifyOrRehydrate: false,
        shouldBeautify: shouldBeautifyLink,
        shouldRehydrate: shouldRehydrate,
        extension: '',
        hasValidFileName: true,
    };

    trace.trace.info(
        `LinkPlugin.getSharingInfoToTryUpdateLink: shouldBeautify=${shouldBeautifyLink} isPreviouslyBeautified=${isPreviouslyBeautified}, anchorElementsSource=${anchorElementsSource}`
    );

    const addedElements: Node[] | null = displaySpinnerWhenShouldBeautifyLink(
        shouldBeautifyLink,
        linkPlugin,
        linkIdsWithSpinner,
        anchorElement
    );
    const originalAnchorElementId = anchorElement.id;
    let linkId: string = getLinkIdFromAnchorElementId(anchorElement.id);

    let sharingLink: SharingLinkInfo = await getSharingLinkInfoAndVerifyHref(anchorElement);
    let response: GetSharingInfoResponseBase;
    let isLinkPastedFromOfficeClient: boolean = false;
    let url: string;
    // After user leaves the compose and comes back, the plugin has been disposed and we need to rerender the UI
    // But we don't need to perform the following tasks
    // 1. send the server call for links we already have the getSharingInformation response in the model store(sharingLink will not be null)
    // 2. we never pass true for shouldBeautifyLink, so we don't need to remove Spinner nor add prefix to id.
    if (!sharingLink) {
        if (pendingLinkIds.indexOf(linkId) !== -1) {
            return;
        }

        datapoint = new PerformanceDatapoint('BeautifyLinkE2E');

        pendingLinkIds.push(linkId);

        // VSO 59895 - this datapoint is depreciated, to be removed once replaced with PerformanceDatapoint in any dashboards/scorecards.
        // used in scope script. VSO 80692 remove after removed from dashboard
        logUsage('AttachmentComposeSPLinks', { anchorElementSource: anchorElementsSource });
        datapoint.addCheckmark('GetSharingInfoStart');

        url = await tryGetUnwrappedUrl(anchorElement, providerType);
        response = await getSharingInformation(
            url,
            anchorElementsSource,
            shouldBeautifyLink,
            shouldRehydrate,
            providerType,
            linkId,
            null /* linkCreatedDateTime */
        );
        datapoint.addCheckmark('GetSharingInfoEnd');
        const extension = getExtensionWithoutDotFromFileNameForLogging(response.FileName);
        customData.extension = extension;
        const index = pendingLinkIds.indexOf(linkId);
        if (index > 0) {
            pendingLinkIds.splice(index, 1);
        }

        removeSpinnerWhenShouldBeautifyLink(
            shouldBeautifyLink,
            linkIdsWithSpinner,
            anchorElement,
            addedElements
        );
        const stopReason: string = stopReasonAfterGetSharingInfo(
            linkPlugin,
            originalAnchorElementId,
            linkIndex,
            anchorElement
        );
        if (stopReason) {
            customData.canceledReason = stopReason;
            datapoint.addCustomData(customData);
            datapoint.end();
            return;
        }

        if (
            providerType === LinkProviderType.OneDriveConsumer &&
            response.SharingLinkKind !== SharingLinkKind.AnonymousView &&
            response.SharingLinkKind !== SharingLinkKind.AnonymousEdit
        ) {
            return;
        }

        // Undo snapshot must be created before the id is prefixed with the special beautified token
        linkPlugin.editor.addUndoSnapshot();

        const isValidResponse: boolean =
            response && response.ResultCode === AttachmentResultCode.Success;
        const hasValidFileName: boolean = !isStringNullOrWhiteSpace(response.FileName);
        customData.hasValidFileName = hasValidFileName;

        // Office client copy link content is a pure anchor element with file name as the text.
        // We have to wait for getSharingInformation response to determine this scenario.
        isLinkPastedFromOfficeClient =
            isPureAnchorElement(anchorElement) &&
            anchorElementsSource === AnchorElementsSource.PasteHtml &&
            isValidResponse &&
            anchorElement.text === response.FileName;

        if (
            (shouldBeautifyLink || isLinkPastedFromOfficeClient) &&
            isValidResponse &&
            hasValidFileName
        ) {
            addBeautifiedPrefixToId(anchorElement);
        }
    } else if (shouldBeautifyLink) {
        trace.errorThatWillCauseAlert(
            'We should not pass true for shouldBeautifyLink when processing the same link again.'
        );
    }

    // The anchorElement id may have changed above
    linkId = getLinkIdFromAnchorElementId(anchorElement.id);

    if (!sharingLink) {
        // Raise the action to add sharingLink into store
        const shouldAddComposeLinkViewState: boolean =
            (shouldBeautifyLink || shouldRehydrate) &&
            ((response && response.ResultCode == AttachmentResultCode.Success) ||
                isPreviouslyBeautified);

        // If getSharingInformation can retrieve creationDateTime, we should pass it to onSharingLinkDetected
        // Then check access can use it to decide whether to send call to RW server
        onSharingLinkDetected(
            linkId,
            anchorElement.href,
            linkIdContainer.sharingLinkIds,
            response,
            shouldAddComposeLinkViewState /* shouldAddComposeLinkViewState */,
            isPreviouslyBeautified,
            anchorElementsSource,
            providerType,
            url !== anchorElement.href ? url : null
        );
    } else if (isPreviouslyBeautified || sharingLink.sharingInfo.getSharingInfoSucceeded) {
        // If sharingLink already exists, we don't need to add it to model store nor update the sharing tips
        // we just need to create ComposeLinkViewState.
        addComposeLinkViewState(linkId);
        addSharingLinkToContianer(anchorElement.id, linkIdContainer.sharingLinkIds);
    }

    // Always try to check recipient permission
    const tryCheckPermForOneLink = await lazyTryCheckPermForOneLink.import();
    tryCheckPermForOneLink(
        linkId,
        linkPlugin.getRecipientWells(),
        linkPlugin.fromAddress,
        linkIdContainer.composeId,
        isCalendar
    );

    // No other UI changes, just leave
    if (!shouldBeautifyLink && !shouldRehydrate) {
        customData.didBeautifyOrRehydrate = false;
        customData.shouldBeautify = false;
        customData.shouldRehydrate = false;
        datapoint?.addCustomData(customData);
        datapoint?.end();
        return;
    }

    // Retrieve sharingLink if we haven't got one.
    if (!sharingLink) {
        sharingLink = getSharingLinkInfo(linkId);
    }

    const isLinkWithValidFileName: boolean =
        sharingLink && !isStringNullOrWhiteSpace(sharingLink.fileName);
    const isValidSharingInfo: boolean =
        sharingLink && sharingLink.sharingInfo.getSharingInfoSucceeded;

    // Beautify or rehydrate the link when conditions are met.
    if (isValidSharingInfo || isPreviouslyBeautified) {
        updateSharePointLinkPermissionInDraft(sharingLink, originalAnchorElementId);

        createSharingLinkView(
            linkPlugin.editor,
            linkPlugin.editorViewState,
            linkPlugin.getRecipientWells,
            linkPlugin.fromAddress,
            linkIdContainer.composeId,
            anchorElement,
            sharingLink,
            isLinkWithValidFileName,
            shouldBeautifyLink || isLinkPastedFromOfficeClient,
            isPreviouslyBeautified,
            targetWindow
        );
        customData.didBeautifyOrRehydrate = true;
        datapoint?.addCustomData(customData);

        await prefetchGetWACInfo(anchorElement.href);
    } else {
        datapoint?.addCustomData(customData);
    }

    datapoint?.end();

    if (
        anchorElementsSource === AnchorElementsSource.PasteHtml ||
        anchorElementsSource === AnchorElementsSource.InsertLinkFromToolbar ||
        anchorElementsSource === AnchorElementsSource.InsertLinkFromAttachMenu ||
        anchorElementsSource === AnchorElementsSource.AutoLink ||
        anchorElementsSource === AnchorElementsSource.LinkContextMenu
    ) {
        const getSharingInfoResponse = response as GetSharingInfoResponse;
        if (getSharingInfoResponse) {
            const sessionSettings = getUserConfiguration().SessionSettings;
            const getAttachmentSuggestionsTraceIdByComposeId = await lazyGetAttachmentSuggestionsTraceIdByComposeId.import();

            lazyLogEntitiesFromFilePicker.importAndExecute(
                SubstrateSearchScenario.SuggestedAttachments,
                sessionSettings.UserPuid,
                sessionSettings.ExternalDirectoryTenantGuid,
                getAttachmentSuggestionsTraceIdByComposeId(linkIdContainer.composeId),
                null /* fileType */,
                JSON.stringify([
                    {
                        ListId: getSharingInfoResponse.ListId,
                        SiteId: getSharingInfoResponse.SiteId,
                        UniqueId: getSharingInfoResponse.ItemUniqueId,
                        WebId: getSharingInfoResponse.WebId,
                    },
                ])
            );
        }
    }
}
